/**
 * Promise-based legal dialog (Privacy Policy / Imprint) using DaisyUI modal.
 *
 * Shows scrollable HTML content in a large modal. Closes on Esc, backdrop
 * click, or the close button. Focus is trapped while open and restored on
 * close, similar to {@link confirmDialog}, whose dialog surface it shares.
 * The document's typography is `.legal-dialog-body` in tailwind.css (WP36).
 *
 * `lang` is the document's language when it differs from the page's (R3): the
 * title and the text carry it, the dialog's own controls stay in the page's.
 */
export function legalDialog(title: string, contentHtml: string, lang?: string): Promise<void> {
  return new Promise<void>(resolve => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement('div');
    overlay.className = 'modal modal-open';
    overlay.style.zIndex = '10000';

    const titleId = `ld-title-${Math.random().toString(36).slice(2, 9)}`;

    const box = document.createElement('div');
    box.className = 'modal-box ndi-dialog flex max-h-[85vh] max-w-3xl flex-col';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', titleId);

    const header = document.createElement('div');
    header.className = 'flex items-center justify-between gap-4 border-b border-(--sdr-line) pb-3';

    const h3 = document.createElement('h3');
    h3.id = titleId;
    h3.className = 'text-[0.9375rem] font-medium';
    h3.textContent = title;
    if (lang) h3.lang = lang;

    const closeX = document.createElement('button');
    closeX.type = 'button';
    closeX.className = 'btn btn-ghost btn-sm btn-square';
    closeX.setAttribute('aria-label', 'Close');
    closeX.innerHTML = '<span class="icon-[material-symbols--close] text-xl" aria-hidden="true"></span>';

    header.appendChild(h3);
    header.appendChild(closeX);

    const body = document.createElement('div');
    body.className = 'legal-dialog-body overflow-y-auto py-4 flex-1';
    body.innerHTML = contentHtml;
    if (lang) body.lang = lang;

    const actions = document.createElement('div');
    actions.className = 'modal-action mt-2';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn btn-sm';
    closeBtn.textContent = 'Close';

    actions.appendChild(closeBtn);
    box.appendChild(header);
    box.appendChild(body);
    box.appendChild(actions);

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';

    overlay.appendChild(box);
    overlay.appendChild(backdrop);

    const focusables: HTMLElement[] = [closeX, closeBtn];

    const cleanup = () => {
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try {
          previouslyFocused.focus();
        } catch {
          /* ignore */
        }
      }
      resolve();
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        cleanup();
        return;
      }
      if (ev.key === 'Tab') {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        const active = document.activeElement;
        if (ev.shiftKey && active === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && active === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };

    closeX.addEventListener('click', cleanup);
    closeBtn.addEventListener('click', cleanup);
    backdrop.addEventListener('click', cleanup);
    document.addEventListener('keydown', onKeydown, true);

    document.body.appendChild(overlay);

    requestAnimationFrame(() => closeBtn.focus());
  });
}

const PRIVACY_POLICY_HTML = `
<p class="lead">Diese Datenschutzerklärung wurde zuletzt am <strong>Januar 3, 2026</strong> aktualisiert und gilt für Bürger und Personen mit ständigem Wohnsitz im Europäischen Wirtschaftsraum und der Schweiz.</p>

<p>In dieser Datenschutzerklärung erklären wir, wie wir mit den Daten umgehen, die wir über
<a href="https://new-digital-intelligence.com" target="_blank" rel="noopener noreferrer">https://new-digital-intelligence.com</a>
von Ihnen erhalten. Wir empfehlen Ihnen, diese Erklärung sorgfältig zu lesen. Bei unserer Verarbeitung beachten wir die datenschutzrechtlichen Bestimmungen. Das bedeutet unter anderem, dass:</p>

<ul>
  <li>wir <strong>geben deutlich an</strong>, zu welchen Zwecken wir personenbezogene Daten verarbeiten. Wir tun dies mit Hilfe dieser Datenschutzerklärung;</li>
  <li>wir sind bestrebt, unsere Erhebung personenbezogener Daten nur auf die zu legitimen Zwecken <strong>erforderlichen</strong> personenbezogenen Daten zu beschränken;</li>
  <li>in Fällen, in denen Ihre Zustimmung erforderlich ist, bitten wir Sie zunächst um Ihre <strong>ausdrückliche Zustimmung</strong> zur Verarbeitung Ihrer personenbezogenen Daten;</li>
  <li>wir ergreifen geeignete <strong>Sicherheitsmaßnahmen</strong>, um Ihre persönlichen Daten zu schützen und fordern dies auch von den Parteien an, die persönliche Daten in unserem Namen verarbeiten;</li>
  <li>wir respektieren Ihr Recht, auf Ihre persönlichen Daten zuzugreifen oder sie auf Ihre Aufforderung hin berichtigen oder löschen zu lassen.</li>
</ul>

<p>Wenn Sie Fragen haben oder genau wissen möchten, welche Daten wir von Ihnen aufbewahren, kontaktieren Sie uns bitte.</p>

<h2>1. Zweck, Daten und Aufbewahrungsdauer</h2>
<p>Wir können personenbezogene Daten für eine Reihe von Zwecken im Zusammenhang mit unserem Geschäftsbetrieb erfassen oder erhalten, zu denen die folgenden gehören können:</p>

<h3>1.1 Kontakt – z.&nbsp;B. über Telefon, Postanschrift, E-Mail und/oder Webformulare</h3>
<p>Zu diesem Zweck verwenden wir folgende Daten:</p>
<ul>
  <li>Ein Vor- und Nachname</li>
  <li>E-Mail-Adresse</li>
  <li>Eine Telefonnummer</li>
  <li>IP-Adresse</li>
</ul>
<p><strong>Rechtsgrundlage:</strong> Nach Erteilung der Zustimmung.</p>
<p><strong>Aufbewahrungszeitraum:</strong> Wir bewahren diese Daten bis zur Beendigung des Dienstes auf.</p>

<h3>1.2 Erstellung und Analyse von Statistiken zur Verbesserung der Website</h3>
<p>Zu diesem Zweck verwenden wir folgende Daten:</p>
<ul>
  <li>Informationen zu Internetaktivitäten, einschließlich Browserverlauf, Suchverlauf und Informationen zur Interaktion eines Verbrauchers mit einer Internetwebsite, -anwendung oder -werbung, ohne darauf beschränkt zu sein</li>
  <li>Geolocation-Daten</li>
</ul>
<p><strong>Rechtsgrundlage:</strong> Sie ist zur Wahrung der berechtigten Interessen des für die Verarbeitung Verantwortlichen oder eines Dritten erforderlich, und dieses Interesse überwiegt die Interessen der betroffenen Person.</p>
<p><strong>Aufbewahrungszeitraum:</strong> Bei Beendigung des Dienstes speichern wir diese Daten für den folgenden Zeitraum: <strong>16 months</strong>.</p>

<h2>2. Cookies</h2>
<p>Unsere Website verwendet Cookies. Weitere Informationen zu Cookies finden Sie in unserer <strong>Cookie-Richtlinie</strong>.</p>

<h2>3. Offenlegungspraktiken</h2>
<p>Wir legen personenbezogene Daten offen, wenn wir <strong>gesetzlich oder per Gerichtsbeschluss</strong> dazu verpflichtet sind, z.&nbsp;B. auf Anforderung einer Strafverfolgungsbehörde, soweit dies im Rahmen der gesetzlichen Bestimmungen zulässig ist, um Auskünfte zu erteilen, oder für eine Untersuchung in einer Angelegenheit, die die öffentliche Sicherheit betrifft.</p>
<p>Wenn unsere Website oder unser Unternehmen im Rahmen einer <strong>Fusion oder Übernahme</strong> übernommen oder verkauft wird, können Ihre Daten unseren Beratern und potenziellen Käufern offengelegt und an die neuen Eigentümer übermittelt werden.</p>

<h2>4. Sicherheit</h2>
<p>Wir verpflichten uns zur Sicherheit personenbezogener Daten. Wir ergreifen geeignete Sicherheitsmaßnahmen, um den <strong>Missbrauch</strong> und den <strong>unbefugten Zugriff</strong> auf personenbezogene Daten zu begrenzen. Dies stellt sicher, dass nur die erforderlichen Personen Zugriff auf Ihre Daten haben, dass der Zugriff auf die Daten geschützt ist und dass unsere Sicherheitsmaßnahmen regelmäßig überprüft werden.</p>

<h2>5. Websites von Drittanbietern</h2>
<p>Diese Datenschutzerklärung gilt <strong>nicht</strong> für Websites Dritter, die durch Links auf unserer Website verbunden sind. Wir können nicht garantieren, dass diese Dritten Ihre personenbezogenen Daten auf zuverlässige oder sichere Weise behandeln. Wir empfehlen Ihnen, die Datenschutzerklärungen dieser Websites zu lesen, bevor Sie diese Websites nutzen.</p>

<h2>6. Änderungen dieser Datenschutzerklärung</h2>
<p>Wir behalten uns das Recht vor, Änderungen an dieser Datenschutzerklärung vorzunehmen. Es wird empfohlen, diese Datenschutzerklärung <strong>regelmäßig zu lesen</strong>, um über Änderungen informiert zu sein. Darüber hinaus werden wir Sie nach Möglichkeit aktiv informieren.</p>

<h2>7. Zugriff auf und Änderung Ihrer Daten</h2>
<p>Wenn Sie Fragen haben oder wissen möchten, welche personenbezogenen Daten wir über Sie haben, wenden Sie sich bitte an uns. Sie können uns unter Verwendung der folgenden Informationen kontaktieren. Sie haben folgende Rechte:</p>
<ul>
  <li><strong>Recht auf Information:</strong> Sie haben das Recht zu erfahren, warum Ihre personenbezogenen Daten benötigt werden, was damit passiert und wie lange sie aufbewahrt werden.</li>
  <li><strong>Auskunftsrecht:</strong> Sie haben das Recht, auf Ihre uns bekannten personenbezogenen Daten zuzugreifen.</li>
  <li><strong>Recht auf Berichtigung:</strong> Sie haben jederzeit das Recht, Ihre personenbezogenen Daten zu ergänzen, zu berichtigen, zu löschen oder zu sperren.</li>
  <li><strong>Widerruf der Einwilligung:</strong> Wenn Sie uns Ihre Einwilligung zur Verarbeitung Ihrer Daten erteilen, haben Sie das Recht, diese Einwilligung zu widerrufen und Ihre personenbezogenen Daten löschen zu lassen.</li>
  <li><strong>Recht auf Datenübertragbarkeit:</strong> Sie haben das Recht, alle Ihre personenbezogenen Daten vom für die Verarbeitung Verantwortlichen anzufordern und vollständig an einen anderen für die Verarbeitung Verantwortlichen zu übermitteln.</li>
  <li><strong>Widerspruchsrecht:</strong> Sie können der Verarbeitung Ihrer Daten widersprechen. Wir halten uns daran, es sei denn, es liegen berechtigte Gründe für die Bearbeitung vor.</li>
</ul>
<p><em>Bitte geben Sie immer deutlich an, wer Sie sind, damit wir sicher sein können, dass wir keine Daten der falschen Person ändern oder löschen.</em></p>

<h2>8. Einreichen einer Beschwerde</h2>
<p>Wenn Sie mit der Art und Weise, wie wir mit der Verarbeitung Ihrer personenbezogenen Daten umgehen, nicht zufrieden sind, haben Sie das Recht, eine <strong>Beschwerde bei der Datenschutzbehörde</strong> einzureichen.</p>

<h2>9. Kontaktdetails</h2>
<address>
  <strong>New Digital Intelligence</strong><br>
  Gotthardstrasse 14<br>
  6300 Zug<br>
  Schweiz<br>
  <strong>Website:</strong> <a href="https://new-digital-intelligence.com" target="_blank" rel="noopener noreferrer">https://new-digital-intelligence.com</a><br>
  <strong>E-Mail:</strong> <a href="mailto:mail@new-digital-intelligence.com">mail@new-digital-intelligence.com</a><br>
  <strong>Telefon:</strong> <a href="tel:+41335333654">+41 33 533 36 54</a>
</address>
`;

const IMPRINT_HTML = `
<p class="lead">Dieses Impressum wurde zuletzt am <strong>Januar 3, 2026</strong> aktualisiert.</p>

<h2>Inhaber dieser Website</h2>
<address>
  <strong>New Digital Intelligence GmbH</strong><br>
  Gotthardstrasse 14<br>
  6300 Zug<br>
  Schweiz<br>
  <strong>E-Mail:</strong> <a href="mailto:mail@new-digital-intelligence.com">mail@new-digital-intelligence.com</a><br>
  <strong>Telefon:</strong> <a href="tel:+41335333654">+41 33 533 36 54</a><br>
  <strong>USt-IdNr.:</strong> CHE-252.517.607 MWST
</address>

<h3>Gesetzliche(r) Vertreter von New Digital Intelligence GmbH</h3>
<p><strong>Michael Burian</strong></p>

<h2>1. Allgemein</h2>
<h3>1.1 Registereintrag</h3>
<p>Wir sind beim <strong>Schweizer Handelsregister</strong> unter der Lizenz- oder Registrierungsnummer registriert:</p>
<p><strong>CHE-252.517.607</strong></p>
<p>Wir sind bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer <strong>Verbraucherschlichtungsstelle</strong> teilzunehmen.</p>

<h2>2. Angaben nach deutscher Gesetzgebung</h2>
<p>Die folgenden Angaben sind nach deutscher Gesetzgebung zwingend erforderlich.</p>

<h3>2.1 Verantwortlich für den Inhalt</h3>
<p>Wir stellen journalistische und redaktionell aufbereitete Inhalte zur Verfügung. Daher müssen wir den Namen und den Wohnort der Person nennen, die für den Inhalt dieser Website verantwortlich ist:</p>
<p><strong>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV:</strong><br>Michael Burian aus Ebsdorfergrund.</p>
`;

export function showPrivacyPolicy(): Promise<void> {
  return legalDialog('Datenschutzerklärung', PRIVACY_POLICY_HTML, 'de');
}

export function showImprint(): Promise<void> {
  return legalDialog('Impressum', IMPRINT_HTML, 'de');
}
